/**
 * Read-only attendance card for department managers.
 *
 * Only renders when:
 *   1. The parent has confirmed the current user is a manager
 *      (`is_manager || is_department_manager`).
 *   2. The global `dept_manager_can_view_attendance` toggle is enabled.
 *   3. The backend returns at least one direct report.
 *
 * The endpoint (`GET /api/attendance/team`) is intentionally minimal —
 * it exposes only name, date, clock-in/out, status. No overtime, no
 * late penalty, no GPS breadcrumbs. Any 403 (feature disabled or no
 * reports) makes the card silently hide.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Users, ClockCounterClockwise, X } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  present:    'bg-emerald-100 text-emerald-700 border-emerald-200',
  late:       'bg-amber-100 text-amber-700 border-amber-200',
  absent:     'bg-red-100 text-red-700 border-red-200',
  on_leave:   'bg-blue-100 text-blue-700 border-blue-200',
  weekend:    'bg-gray-100 text-gray-600 border-gray-200',
  holiday:    'bg-purple-100 text-purple-700 border-purple-200',
};

const STATUS_LABEL_AR = {
  present: 'حاضر', late: 'متأخر', absent: 'غائب',
  on_leave: 'إجازة', weekend: 'راحة', holiday: 'عطلة',
};
const STATUS_LABEL_EN = {
  present: 'Present', late: 'Late', absent: 'Absent',
  on_leave: 'On Leave', weekend: 'Weekend', holiday: 'Holiday',
};

export default function ManagerTeamAttendanceCard({ language = 'ar' }) {
  const isAr = language === 'ar';
  const [records, setRecords] = useState([]);
  const [reportsCount, setReportsCount] = useState(0);
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  // Date filter — when set, the endpoint returns records for that exact
  // day only. Empty string = default view (last 30 days).
  const [dateFilter, setDateFilter] = useState('');

  const fetchTeam = useCallback(async (dateStr = '') => {
    try {
      const params = dateStr ? { date: dateStr } : {};
      const { data } = await axios.get(`${API}/attendance/team`, {
        params,
        withCredentials: true,
      });
      setRecords(data?.records || []);
      setReportsCount(data?.reports_count || 0);
      setEnabled(true);
    } catch (e) {
      // 403 → toggle disabled OR no direct reports. Keep the card hidden.
      setEnabled(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTeam(dateFilter); }, [fetchTeam, dateFilter]);

  if (loading || !enabled) return null;

  return (
    <Card className="border border-[#C4BFF0]" data-testid="team-attendance-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <ClockCounterClockwise size={22} weight="duotone" className="text-[#655dc6]" />
            <h3 className="text-lg font-semibold text-[#655dc6]">
              {isAr ? 'حضور وانصراف موظفي القسم' : 'Team Attendance'}
            </h3>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Date filter — a single-day view. Clear to fall back to
                the default last-30-days list. */}
            <label className="text-xs text-[#655dc6]/80 whitespace-nowrap">
              {isAr ? 'التاريخ:' : 'Date:'}
            </label>
            <Input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="h-8 w-[160px] text-xs bg-white border-[#C4BFF0] text-[#655dc6]"
              data-testid="team-att-date-filter"
            />
            {dateFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDateFilter('')}
                className="h-8 w-8 p-0 text-[#655dc6]/70 hover:bg-[#F2F0FA]"
                title={isAr ? 'مسح الفلتر (عرض آخر 30 يوم)' : 'Clear filter (last 30 days)'}
                data-testid="team-att-clear-filter"
              >
                <X size={14} />
              </Button>
            )}
            <div className="flex items-center gap-2 text-xs text-[#655dc6]/70 ms-2">
              <Users size={14} />
              <span>{isAr ? `${reportsCount} موظف` : `${reportsCount} employees`}</span>
              <Badge variant="outline" className="text-[10px]">
                {isAr ? 'قراءة فقط' : 'Read-only'}
              </Badge>
            </div>
          </div>
        </div>

        {records.length === 0 ? (
          <p className="text-[#655dc6]/50 text-sm text-center py-6">
            {dateFilter
              ? (isAr ? `لا توجد سجلات في تاريخ ${dateFilter}` : `No records for ${dateFilter}`)
              : (isAr ? 'لا توجد سجلات حضور مؤخراً' : 'No recent attendance records')}
          </p>
        ) : (
          <div className="overflow-x-auto max-h-[420px]">
            <Table className="[&_th]:text-center [&_td]:text-center">
              <TableHeader className="sticky top-0 bg-[#F5F4FC] z-10">
                <TableRow>
                  <TableHead className="text-[#655dc6]">{isAr ? 'الموظف' : 'Employee'}</TableHead>
                  <TableHead className="text-[#655dc6]">{isAr ? 'التاريخ' : 'Date'}</TableHead>
                  <TableHead className="text-[#655dc6]">{isAr ? 'الحضور' : 'Clock In'}</TableHead>
                  <TableHead className="text-[#655dc6]">{isAr ? 'الانصراف' : 'Clock Out'}</TableHead>
                  <TableHead className="text-[#655dc6]">{isAr ? 'الحالة' : 'Status'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map((r, idx) => (
                  <TableRow key={`${r.user_id}_${r.date}_${idx}`} data-testid={`team-att-row-${idx}`}>
                    <TableCell className="text-center font-medium text-[#655dc6]">{r.employee_name}</TableCell>
                    <TableCell className="text-center text-xs text-[#655dc6]/70">
                      <span dir="ltr" className="inline-block">{r.date}</span>
                    </TableCell>
                    <TableCell className="text-center text-xs">
                      <span dir="ltr" className="inline-block">
                        {r.clock_in ? new Date(r.clock_in).toLocaleTimeString(isAr ? 'ar-SA' : 'en-US', { hour: '2-digit', minute: '2-digit' }) : '—'}
                      </span>
                    </TableCell>
                    <TableCell className="text-center text-xs">
                      <span dir="ltr" className="inline-block">
                        {r.clock_out ? new Date(r.clock_out).toLocaleTimeString(isAr ? 'ar-SA' : 'en-US', { hour: '2-digit', minute: '2-digit' }) : '—'}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className={`text-[10px] ${STATUS_STYLES[r.status] || 'bg-gray-100 text-gray-700'}`}>
                        {(isAr ? STATUS_LABEL_AR : STATUS_LABEL_EN)[r.status] || r.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
